/**
 * services/estimateMath.ts — Canonical per-estimate money math.
 *
 * Single source of truth for an estimate's subtotal, tax, grand total, cost
 * basis, profit and margin. Pure function — no React, no Supabase, no side
 * effects. Trivially testable.
 *
 * WHY THIS EXISTS:
 *   Before this module, three screens each recomputed margin/cost their own
 *   way and disagreed for the SAME estimate:
 *     - hooks/useEstimateForm.ts  (the builder — pre-tax revenue, breakEvenRate
 *                                  cost basis, trip cost ×0.75, discount +
 *                                  difficulty + min-fee all applied)
 *     - views/EstimatesView.tsx   (the list "Profit %" column — ignored
 *                                  breakEvenRate, trip cost ×0.5, ignored
 *                                  discount/difficulty/min-fee)
 *     - components/EstimateDetail.tsx (the detail drawer — used est.total which
 *                                  INCLUDES TAX as the revenue denominator)
 *   So the list showed one margin, the drawer another, the builder a third.
 *   All three now call computeEstimateTotals() and agree by construction.
 *
 * Consumers (must NOT duplicate this math):
 *   - hooks/useEstimateForm.ts        (live totals while editing)
 *   - views/EstimatesView.tsx         (list Profit % column)
 *   - components/EstimateDetail.tsx   (detail drawer financial card)
 */

import type { EstimateItem, EstimateAdjustments, PricingSnapshot, SiteConditions } from '../types';
import { isPassThroughType } from '../utils/passThroughPricing';

/**
 * Qué parte del cargo por viaje es costo real (camioneta, combustible,
 * traslado) y no ganancia. El resto queda como margen del traslado.
 *
 * Es una estimación conservadora, no una medición: sube el costo y por tanto
 * BAJA el margen reportado, que es el lado seguro para equivocarse. Está aquí
 * con nombre —y no como un 0.75 suelto a media función— porque cambiarla mueve
 * el margen de todos los presupuestos que lleven viaje activado, y eso debe
 * ser una decisión visible.
 */
const TRIP_CHARGE_COST_SHARE = 0.75;

/**
 * Qué parte del recargo por emergencia es costo (el sobretiempo que se le paga
 * a la cuadrilla). El resto es prima por la urgencia.
 *
 * Al revés que el anterior: como es menor que 1, activar el recargo SUBE el
 * margen reportado. Misma advertencia — tocarla re-precia el histórico.
 */
const EMERGENCY_SURCHARGE_COST_SHARE = 0.66;

/** The slice of BusinessSettings.rules this math depends on. */
export interface EstimateMathRules {
  tripCharge: number;
  emergencySurchargePercent: number;
  minServiceCall: number;
}

export interface EstimateMathInput {
  items: EstimateItem[];
  adjustments?: Partial<EstimateAdjustments>;
  siteConditions?: Pick<SiteConditions, 'difficultyFactor'>;
  pricingSnapshot: PricingSnapshot;
  rules: EstimateMathRules;
}

export interface EstimateTotals {
  // Revenue build-up
  itemsTotal: number;
  laborItemsTotal: number;
  nonLaborItemsTotal: number;
  laborSurcharge: number;
  laborDifficultySurcharge: number;
  tripFee: number;
  discountAmount: number;
  subTotal: number;
  minJobGap: number;
  finalMinFee: number;
  taxAmount: number;
  grandTotal: number;            // includes tax — what gets persisted as estimate.total
  // Cost / profit (all pre-tax — tax is a pass-through, never profit)
  laborCostTotal: number;
  materialCostTotal: number;
  totalCost: number;
  revenuePreTax: number;         // subTotal + finalMinFee — the margin denominator
  netProfit: number;
  marginPercent: number;
  // --- Segunda lectura: el margen del TRABAJO PROPIO (pase directo fuera) ---
  //
  // Dos lecturas del MISMO cálculo, no dos cálculos. `marginPercent` es el
  // facturado (todo dentro) y no cambia de valor; estos cinco campos son
  // aditivos y nacen aquí para que ninguna pantalla vuelva a dividir por su
  // cuenta — que es exactamente cómo tres pantallas acabaron enseñando tres
  // márgenes distintos del mismo presupuesto.
  passThroughRevenue: number;    // lo facturado en filas de pase directo (Fee)
  passThroughCost: number;       // su costo — idéntico al ingreso, ver más abajo
  revenueOwnWork: number;        // revenuePreTax - passThroughRevenue
  netProfitOwnWork: number;      // === netProfit SIEMPRE (el pase directo no gana nada)
  marginPercentOwnWork: number;  // netProfitOwnWork / revenueOwnWork * 100
  // Deposit
  depositPercent: number;
  depositAmount: number;
}

/**
 * Reconstruct the fully-loaded hourly LABOR cost from a pricing snapshot.
 * Prefers the frozen break-even rate (the complete company cost / billable
 * hour); falls back to wage×(1+burden)+overhead for older snapshots that
 * predate breakEvenRate being captured.
 */
export function laborCostPerHourFromSnapshot(snap: PricingSnapshot): number {
  // `> 0`, no `??`.
  //
  // El `??` solo cae al respaldo con null/undefined, así que un breakEvenRate
  // de 0 pasaba como válido — y services/metrics.ts devuelve exactamente 0
  // cuando no hay horas facturables (taller recién configurado, nadie con
  // utilización > 0). Con eso, TODA la mano de obra costaba $0: 40 horas-hombre
  // a $42,86 daban $1.714,29 de ingreso, $0,00 de costo y un margen del 100%,
  // mientras la columna COSTO de esa misma fila seguía mostrando $1.200. El
  // contratista podía rebajar hasta $1.200 creyendo que aún ganaba 30%.
  //
  // Las gemelas de CategoryTemplatesView y PrivateTemplateEditor sí guardaban
  // contra cero; esta no. Ahora es la única implementación y las demás la
  // importan.
  if (Number.isFinite(snap.breakEvenRate) && (snap.breakEvenRate as number) > 0) {
    return snap.breakEvenRate as number;
  }
  const wage = Number.isFinite(snap.baseLaborCost) ? snap.baseLaborCost : 0;
  const burden = Number.isFinite(snap.laborBurdenPercent) ? snap.laborBurdenPercent : 0;
  const overhead = Number.isFinite(snap.overheadPerManHour) ? snap.overheadPerManHour : 0;
  return (wage * (1 + burden / 100)) + overhead;
}

/**
 * El costo de UNA fila de mano de obra, por unidad de cantidad.
 *
 * Prefiere el costo que la propia fila declara —que es el que la pantalla pinta
 * en la columna COSTO— y solo cae al snapshot cuando la fila no trae ninguno.
 *
 * ANTES el motor ignoraba `item.cost` siempre y costeaba todo con el snapshot.
 * Eso hacía que la columna COSTO de una fila de labor fuera decorativa: subir
 * el break-even de $51,77 a $62,00 y pulsar Refrescar hundía la ganancia de
 * $41,85 a $26,50 y el margen de 30,0% a 19,0%, mientras las filas seguían
 * enseñando $51,77. Los números de la pantalla ya no podían explicar el
 * número de la cabecera.
 */
function laborCostOfItem(item: EstimateItem, snapshotCostPerHour: number): number {
  const declared = item.cost;
  if (Number.isFinite(declared) && (declared as number) > 0) return declared as number;
  return snapshotCostPerHour;
}

/** Un número utilizable, o 0. Evita que un campo ausente convierta todo en NaN. */
function num(value: number | undefined | null): number {
  return Number.isFinite(value as number) ? (value as number) : 0;
}

/**
 * Canonical estimate totals. Ported verbatim from the builder's original math
 * so the builder's displayed numbers are unchanged; the list and detail views
 * now converge onto this same result.
 */
/**
 * El recargo por condiciones del terreno, en dólares.
 *
 * Exportado aparte porque la propuesta del cliente también lo necesita —lo pinta
 * como una línea con el nombre que el contratista le ponga— y no puede llamar a
 * `computeEstimateTotals`: la vista pública no tiene ni pricingSnapshot ni las
 * reglas del taller. Copiar la fórmula allí sería garantizar que un día las dos
 * dejen de coincidir y el documento del cliente enseñe un número que la app no
 * reconoce.
 */
export function laborDifficultySurchargeOf(items: EstimateItem[], difficultyFactor?: number): number {
  const factor = difficultyFactor ?? 1.0;
  if (!(factor > 1.0)) return 0;
  const laborItemsTotal = (items || [])
    .filter(i => i.type === 'Labor')
    .reduce((acc, curr) => acc + (curr.total || 0), 0);
  return laborItemsTotal * (factor - 1.0);
}

export function computeEstimateTotals(input: EstimateMathInput): EstimateTotals {
  const { pricingSnapshot } = input;
  const items = input.items ?? [];
  const rules = input.rules;
  const adjustments = input.adjustments ?? {};
  const difficultyFactor = input.siteConditions?.difficultyFactor ?? 1.0;

  // 1. Base totals
  const itemsTotal = items.reduce((acc, curr) => acc + num(curr.total), 0);
  const laborItemsTotal = items.filter(i => i.type === 'Labor').reduce((acc, curr) => acc + num(curr.total), 0);
  const nonLaborItemsTotal = items.filter(i => i.type !== 'Labor').reduce((acc, curr) => acc + num(curr.total), 0);

  // 2. Adjustments (surcharges add to revenue)
  const laborSurcharge = adjustments.emergencySurcharge
    ? laborItemsTotal * (num(rules.emergencySurchargePercent) / 100)
    : 0;
  const tripFee = adjustments.tripCharge ? num(rules.tripCharge) : 0;

  // 2b. Site difficulty surcharge — applies difficultyFactor to labor revenue
  const laborDifficultySurcharge = laborDifficultySurchargeOf(items, difficultyFactor);

  // 3. Discount — clamped so it can never exceed the pre-discount subtotal,
  //    preventing a negative subTotal / grandTotal.
  const preDiscountSubtotal = itemsTotal + tripFee + laborSurcharge + laborDifficultySurcharge;

  /* El pase directo, calculado AQUÍ y no más abajo: la base del descuento lo
   * necesita, y un `const` no se puede leer antes de su línea. */
  const passThroughRevenue = items
    .filter(i => isPassThroughType(i.type))
    .reduce((acc, curr) => acc + num(curr.total), 0);

  /* La base del descuento EXCLUYE el pase directo.
   *
   * Un permiso de $400 se remite a costo: no hay margen del que descontar, y
   * rebajarlo significa poner dinero propio para pagar el recibo de un tercero.
   * Antes la base era el subtotal entero, y eso rompía la decisión 1 del plan
   * —«el permiso del cliente no debe mover TU precio»— justo donde más se nota:
   * con un descuento del 10%, el dial de margen neto pedía k = 1,2162 con un
   * permiso de $400 y k = 2,0531 con uno de $40.000. El contratista subía sus
   * precios un 69% más porque el ayuntamiento cobraba caro.
   *
   * Con la base limpia, el descuento sale solo del trabajo propio, el pase
   * directo pasa entero, y `revenueOwnWork` vuelve a ser AFÍN en k — que es lo
   * que el solve en forma cerrada necesita (Parte A.4).
   *
   * Alcance del cambio en producción, comprobado antes de tocarlo: de 364
   * estimados, 4 llevan descuento Y pase directo, y los 4 con descuento PLANO
   * —donde la base solo decide el tope, no el importe—. Con descuento
   * porcentual y pase directo: ninguno. Ningún total guardado se mueve. */
  const preDiscountOwnWork = preDiscountSubtotal - passThroughRevenue;
  const discountValue = num(adjustments.discount);
  const rawDiscount = discountValue > 0
    ? (adjustments.discountType === 'percent'
      ? preDiscountOwnWork * (discountValue / 100)
      : discountValue)
    : 0;
  /* El tope se aplica solo cuando hay algo que topar. Con un subtotal previo
   * NEGATIVO —posible: el precio de una fila no tiene mínimo, así que una
   * línea de crédito de -$100 entra— `Math.min(0, -100)` devolvía -100, o sea
   * un descuento negativo, y el crédito desaparecía dejando el total en $0,00
   * sin una palabra. Un subtotal negativo no admite descuento: es 0. */
  const discountAmount = preDiscountSubtotal > 0
    ? Math.min(rawDiscount, preDiscountSubtotal)
    : 0;

  const subTotal = preDiscountSubtotal - discountAmount;
  const minJobGap = Math.max(0, num(rules.minServiceCall) - subTotal);
  const finalMinFee = adjustments.minJobFeeApplied ? minJobGap : 0;

  // 4. Tax
  //
  // La base no-labor se prorratea con el descuento. ANTES se usaba
  // `nonLaborItemsTotal` tal cual, que se calcula ANTES de descontar (línea
  // ~114), así que se cobraba impuesto sobre dinero que el cliente no pagó:
  // labor $1.000 + material $1.000 con 20% de descuento y 8,25% facturaba
  // $82,50 en vez de $66,00. Con un descuento plano de $2.000 el trabajo
  // quedaba en $0 y aun así se facturaban $82,50 de impuesto sobre un trabajo
  // regalado. Y `taxLabor: false` es el valor por defecto de TODO presupuesto,
  // así que esta es la rama normal, no un caso raro.
  const discountRatio = preDiscountSubtotal > 0
    ? Math.min(Math.max(discountAmount / preDiscountSubtotal, 0), 1)
    : 0;
  const nonLaborAfterDiscount = nonLaborItemsTotal * (1 - discountRatio);
  const taxableAmount = adjustments.taxLabor ? (subTotal + finalMinFee) : nonLaborAfterDiscount;
  const taxAmount = adjustments.applyTax ? taxableAmount * (num(adjustments.taxRate) / 100) : 0;

  // 5. Grand total (this is what persists as estimate.total)
  const grandTotal = subTotal + finalMinFee + taxAmount;

  // 6. Cost & profit — REAL breakdown from item data
  const laborCostPerHour = laborCostPerHourFromSnapshot(pricingSnapshot);
  let laborCostTotal = 0;
  let materialCostTotal = 0;
  items.forEach(item => {
    if (item.type === 'Labor') {
      laborCostTotal += num(item.quantity) * laborCostOfItem(item, laborCostPerHour);
    } else {
      materialCostTotal += num(item.cost) * num(item.quantity);
    }
  });
  // Trip charge vehicle cost (conservative: 75% is cost)
  if (adjustments.tripCharge) laborCostTotal += rules.tripCharge * TRIP_CHARGE_COST_SHARE;
  // Emergency surcharge (OT pay, ~66% is cost)
  if (adjustments.emergencySurcharge) laborCostTotal += laborSurcharge * EMERGENCY_SURCHARGE_COST_SHARE;

  const totalCost = laborCostTotal + materialCostTotal;
  const revenuePreTax = subTotal + finalMinFee;
  const netProfit = revenuePreTax - totalCost;
  /* Ingreso 0 con costo 0 es un presupuesto vacío: 0% es la lectura correcta.
   * Ingreso 0 con costo > 0 es un desastre —se regaló trabajo que costó
   * dinero— y devolver 0% lo pintaba como un empate. -100% es lo que es. */
  const marginPercent = revenuePreTax > 0
    ? (netProfit / revenuePreTax) * 100
    : (totalCost > 0 ? -100 : 0);

  // 6b. La segunda lectura: el margen del trabajo propio.
  //
  // POR QUÉ DOS. El dial de margen neto persigue lo que el contratista gana con
  // SU trabajo; la cabecera, además, quiere enseñar lo facturado. Un permiso de
  // $40.000 remitido a costo no le da un centavo de ganancia, pero hunde el
  // porcentaje facturado a 3,75% y, si el dial persiguiera ESE número, obligaría
  // a subir el precio de la mano de obra para compensar un recibo que no es
  // suyo. Excluyendo el pase directo, el factor k del solve sale IDÉNTICO
  // (1,087) con un permiso de $400 y con uno de $40.000: el porcentaje vuelve a
  // ser comparable entre trabajos.
  //
  // QUIÉN ES PASE DIRECTO: solo lo que diga isPassThroughType. Una regla, un
  // sitio — la duplicación de esta misma regla ya provocó que un tirón del
  // deslizador deshiciera el pase directo de las plantillas.
  // (`passThroughRevenue` se calcula arriba, antes del descuento: su base lo necesita.)
  /* Su costo NO se recalcula desde item.cost: es el mismo número. Un pase
   * directo se cobra a costo por definición (utils/passThroughPricing emite
   * rate === cost), así que ingreso y costo son la misma cifra y su aporte al
   * beneficio es CERO. Derivarlo de aquí y no de otra suma es lo que hace que
   * `netProfitOwnWork === netProfit` sea estructural en vez de casual.
   * Si alguien edita a mano un Fee y le mete margen, esos dólares NO
   * desaparecen: siguen contados en netProfit y visibles en ambos márgenes —
   * preferimos una anomalía a la vista que un beneficio escondido. */
  const passThroughCost = passThroughRevenue;
  /* Se resta el importe COMPLETO de las filas Fee, sin prorratear el descuento.
   * Además de ser lo que dice el modelo, mantiene revenueOwnWork AFÍN en k —el
   * pase directo nunca escala, así que es un sustraendo constante—, y de eso
   * depende el solve cerrado. Prorratear un descuento PLANO metería a k en el
   * denominador del prorrateo y rompería la afinidad. */
  const revenueOwnWork = revenuePreTax - passThroughRevenue;
  const costOwnWork = totalCost - passThroughCost;
  const netProfitOwnWork = netProfit - (passThroughRevenue - passThroughCost);
  /* Mismos bordes que marginPercent, pero medidos contra el costo PROPIO: un
   * presupuesto que es solo un permiso tiene ingreso propio 0 y costo propio 0
   * —un mandado, ni ganancia ni desastre— y debe leer 0%, no -100%. */
  const marginPercentOwnWork = revenueOwnWork > 0
    ? (netProfitOwnWork / revenueOwnWork) * 100
    : (costOwnWork > 0 ? -100 : 0);

  // 7. Deposit (display-only)
  const depositPercent = Math.max(0, Math.min(100, adjustments.depositPercent ?? 0));
  const depositAmount = grandTotal * (depositPercent / 100);

  return {
    itemsTotal,
    laborItemsTotal,
    nonLaborItemsTotal,
    laborSurcharge,
    laborDifficultySurcharge,
    tripFee,
    discountAmount,
    subTotal,
    minJobGap,
    finalMinFee,
    taxAmount,
    grandTotal,
    laborCostTotal,
    materialCostTotal,
    totalCost,
    revenuePreTax,
    netProfit,
    marginPercent,
    passThroughRevenue,
    passThroughCost,
    revenueOwnWork,
    netProfitOwnWork,
    marginPercentOwnWork,
    depositPercent,
    depositAmount,
  };
}
